import React, { useEffect, useMemo, useRef, useState } from "react";
import PropTypes from "prop-types";
import { Alert, Badge, Button, Card, CardBody, CardHeader, CardTitle, Col, Input, Label, Row } from "reactstrap";
import { ThreeDots } from "react-loader-spinner";

import { resolveMachineRouting } from "../../services/capabilitiesService";
import { postChangeset } from "./shared/captureDeviceService";
import buildChangeset from "./shared/changeset";
import { MAX_IN_FLIGHT, captureFrame, deviceTarget, discoverChannels, runPool } from "./shared/dashcamScan";
import ProbeFrame from "./shared/ProbeFrame";
import resolveDeviceServerName from "./shared/resolveServerName";
import deriveSuffixTemplate from "./shared/suffixTemplate";
import buildDiscoveredCamera, { SUFFIX_KINDS, expandSuffix } from "./shared/discoveredCamera";

/**
 * Camera discovery on a capture device — the camera-model replacement for the legacy
 * "Scan DVR" card of the removed legacy recorder UI.
 *
 * Named `discover*` on purpose: `cameraModel/CameraModelScreenshots.jsx` already owns
 * `triggerDeviceScan` / `scanSignals`, which mean "capture screenshots of the cameras that
 * already exist". This panel does the opposite — it probes slots that carry no `Camera` row
 * yet and turns the ones that answered into cameras.
 *
 * The flow is: probe a slot range through dashcam (a DVR is asked for its channels, an IP-kind
 * device is dialled on its suffix) → pick the slots that produced an image → one
 * `POST /camera-model/changeset/` creating a camera and its single view per pick. Nothing
 * here writes to the legacy `Channel` model.
 */

/** Every slot costs one capture, at most `MAX_IN_FLIGHT` at a time, hence the `1-32` default. */
const MAX_SLOTS = 256;
/** Width of the column a discovered camera is written to — `screenshots.py::MAX_SCAN_SUFFIX_LENGTH`. */
const MAX_SUFFIX_LENGTH = 512;
/** Characters refused in a suffix — mirrors the server's `SHELL_UNSAFE_CHARACTERS`. */
const SHELL_UNSAFE = ["'", '"', "`", "\\", "|", ";", "<", ">", "$"];
const SLOT_PLACEHOLDER = "{}";
const DEFAULT_RANGES = "1-32";

/**
 * Connection parameters the operator may override for a scan, mapped to their scan-body field.
 *
 * Parity with the legacy Scan DVR card, which pre-filled all of them from the `VideoRecorder` row
 * and let them be edited: that is how a device whose stored credentials are wrong gets probed at
 * all. Overriding here never writes back to the device.
 */
const NUMERIC_CONNECTION_FIELDS = ["rtsp_port"];
const TEXT_CONNECTION_FIELDS = ["host", "username", "password", "brand_kind"];

/**
 * Overrides that change *what was probed*, as opposed to how it was reached.
 *
 * A camera row inherits its address and brand from the capture device, so frames obtained with
 * another address or another brand class cannot be turned into rows on this device: they would
 * describe a box nobody probed, and the "existing slot" lock was computed for the stored address
 * too. Creating is therefore refused while one of these is set — the scan stays available, since
 * probing a wrong-credentials or wrong-address hypothesis is exactly what this is for.
 */
const IDENTITY_OVERRIDES = ["host", "rtsp_port", "brand_kind"];

/**
 * Whether the suffix field is worth offering for a brand kind.
 *
 * Fails open on an unresolved kind (the brands list failed to load, or the device's brand id
 * is not in it): hiding the field on a device that turns out to be IP produces a scan with no
 * usable URL, which is worse than an inert field on a branded DVR.
 *
 * @param {string|null|undefined} brandKind - The `RecorderBrand.kind` of the device's brand.
 * @returns {boolean} True when the field should be shown.
 */
function suffixApplies(brandKind) {
  if (brandKind == null || brandKind === "") return true;
  return SUFFIX_KINDS.includes(String(brandKind).toUpperCase());
}

/**
 * @param {string} ranges - A comma-separated list of slots and inclusive `a-b` ranges.
 * @returns {{slots: number[], error: string|null}} The expanded slots, or the reason why not.
 */
function parseSlotRanges(ranges) {
  const text = String(ranges ?? "").trim();
  if (!text) return { slots: [], error: `Slot range is mandatory (e.g. ${DEFAULT_RANGES}).` };

  const slots = new Set();
  const parts = text.split(",");
  for (let index = 0; index < parts.length; index += 1) {
    const part = parts[index].trim();
    if (!part) continue;
    if (part.includes("-")) {
      // `split(_, 2)` truncates instead of keeping the remainder, so "1-2-3" would silently read
      // as 1-2 here while the backend's `split("-", 1)` gives it `int("2-3")` and a 400. Refuse
      // it rather than probe something other than what was typed.
      if ((part.match(/-/g) || []).length > 1) {
        return { slots: [], error: `Could not read "${part}" — use e.g. 1-32 or 1,3,5-8.` };
      }
      const [rawStart, rawEnd] = part.split("-", 2);
      if (!/^\d+$/.test(rawStart.trim()) || !/^\d+$/.test(rawEnd.trim())) {
        return { slots: [], error: `Could not read "${part}" — use e.g. 1-32 or 1,3,5-8.` };
      }
      const start = Number(rawStart);
      const end = Number(rawEnd);
      if (end < start) return { slots: [], error: `"${part}" ends before it starts.` };
      // Bound the span before expanding it, like the backend does.
      if (end - start + 1 > MAX_SLOTS) return { slots: [], error: `Slot range too large (max ${MAX_SLOTS}).` };
      for (let slot = start; slot <= end; slot += 1) slots.add(slot);
    } else {
      if (!/^\d+$/.test(part)) return { slots: [], error: `Could not read "${part}" — use e.g. 1-32 or 1,3,5-8.` };
      slots.add(Number(part));
    }
    if (slots.size > MAX_SLOTS) return { slots: [], error: `Too many slots (max ${MAX_SLOTS}).` };
  }

  const expanded = [...slots].sort((a, b) => a - b);
  if (expanded.length === 0) return { slots: [], error: `Slot range is mandatory (e.g. ${DEFAULT_RANGES}).` };
  if (expanded[0] < 1) return { slots: [], error: "Slot numbers must be positive." };
  return { slots: expanded, error: null };
}

/**
 * Client-side mirror of `screenshots.py::validate_scan_suffix`.
 *
 * `?`, `&`, `=` and `%` are deliberately allowed — they are common and legal in an RTSP path.
 *
 * @param {string} suffix - The raw suffix, possibly empty.
 * @returns {string|null} The reason it is invalid, or null when it is fine.
 */
function validateSuffix(suffix) {
  const text = String(suffix ?? "");
  if (text === "") return null;
  if (text.length > MAX_SUFFIX_LENGTH) return `RTSP suffix is limited to ${MAX_SUFFIX_LENGTH} characters.`;
  if (!text.startsWith("/")) return "RTSP suffix must start with '/'.";
  if (/\s/.test(text)) return "RTSP suffix must not contain whitespace.";
  const forbidden = SHELL_UNSAFE.filter((char) => text.includes(char));
  if (forbidden.length > 0) return `RTSP suffix must not contain ${forbidden.map((c) => `'${c}'`).join(" ")}.`;
  // The suffix is expanded per slot and persisted literal: exactly one `{}`, nothing else brace-shaped.
  const placeholders = text.split(SLOT_PLACEHOLDER).length - 1;
  if (placeholders > 1) return "RTSP suffix accepts at most one '{}' placeholder.";
  if (text.replace(/\{\}/g, "").includes("{") || text.replace(/\{\}/g, "").includes("}")) {
    return "RTSP suffix accepts no brace other than a single '{}'.";
  }
  return null;
}

/**
 * The reason the changeset endpoint refused the batch, attributed to a slot.
 *
 * `ChangesetView` answers a 422 with `{"errors": [{op_id, code, message, field?, details?}]}` and
 * no top-level `message`, so the reason has to be dug out of that list. It always holds exactly
 * one entry: the batch is atomic and raises on the first failing op, which means one collision
 * loses every selected slot. Naming the slot matters more here than anywhere else — the server's
 * uniqueness rule is *wider* than this panel's "existing" lock (`CameraSerializer.validate`
 * matches on store + host + rtsp_port + slot + inference_machine, across devices), so the culprit
 * can be a slot the grid showed as free. Worse, the `IntegrityError` path sends the literal
 * "database constraint violation", which on its own tells the operator nothing at all.
 *
 * @param {object} error - The rejected axios error.
 * @param {Array<object>} [ops] - The ops that were sent, used to resolve `op_id` → `client_ref`.
 * @param {Map<string, number>} [slotByRef] - `client_ref` → slot number.
 * @returns {string} A displayable reason, falling back to a generic one.
 */
function changesetErrorMessage(error, ops, slotByRef) {
  const data = error?.response?.data;
  const first = Array.isArray(data?.errors) ? data.errors[0] : null;
  const message = errorText(first?.message ?? data, "Failed to create the discovered cameras.");
  const culprit = (ops || []).find((op) => op.op_id === first?.op_id);
  // Two lookups, not `client_ref ?? parent_ref`: a create op always carries a `client_ref`, so
  // coalescing before the lookup can never reach `parent_ref` — and a view op's own ref is not a
  // slot key. `buildChangeset` interleaves [cam, view, cam, view], so the failing op is a view
  // whenever the view's INSERT is the one that trips.
  const slot = slotByRef?.get(culprit?.client_ref) ?? slotByRef?.get(culprit?.parent_ref);
  // Nothing was created either way: the whole batch rolled back with this one op.
  return slot == null ? message : `Slot ${slot} was refused, so nothing was created: ${message}`;
}

/**
 * Reduce an error body to a string safe to render.
 *
 * Errors arrive as a bare string or a `{detail: …}` dict, and putting a dict into state raises
 * "Objects are not valid as a React child". Only those two shapes are handled: anything else falls
 * back rather than being guessed at.
 *
 * @param {*} body - The candidate message.
 * @param {string} fallback - What to show when nothing usable can be extracted.
 * @returns {string} A displayable string.
 */
function errorText(body, fallback) {
  if (typeof body === "string" && body.trim()) return body;
  const detail = body?.detail ?? body?.message;
  return typeof detail === "string" && detail.trim() ? detail : fallback;
}

/** TEMP ids let `buildChangeset` emit creates and wire `parent_ref` to a `client_ref`. */
let tempCounter = 0;
function tempId(prefix) {
  tempCounter += 1;
  return `${prefix}_TEMP_${Date.now()}_${tempCounter}`;
}

export default function DeviceDiscoveryPanel({
  storeId,
  device,
  tree,
  machineCache,
  brandKind,
  brandKinds,
  onApplied,
}) {
  const [ranges, setRanges] = useState(DEFAULT_RANGES);
  // Connection parameters, pre-filled from the device and editable for the scan only. `null` means
  // "untouched": the field shows the device's value and no override travels in the request.
  const [overrides, setOverrides] = useState({});
  const [streamQuality, setStreamQuality] = useState("SUB");
  const [protocol, setProtocol] = useState("TCP");
  const [busy, setBusy] = useState(false);
  const [creating, setCreating] = useState(false);
  // null = nothing probed yet; distinguishes "no camera found" from the initial state.
  const [results, setResults] = useState(null);
  const [scan, setScan] = useState(null);
  const [misses, setMisses] = useState([]);
  const [selected, setSelected] = useState({});
  const [failure, setFailure] = useState(null);

  /**
   * The suffix template rebuilt from the device's existing cameras.
   *
   * A GENERIC_IP camera created with an empty suffix has no stream path. The template is derived
   * rather than stored, so it is recomputed from the tree.
   */
  const derivedSuffix = useMemo(() => deriveSuffixTemplate(device?.cameras), [device]);

  // The host unmounts this panel whenever the operator re-clicks "Discover cameras", which can
  // happen while a scan or a create is still in flight. Same guard as the brands effect in
  // CameraModelScreenshots: drop the late result instead of setting state on a dead component.
  const mounted = useRef(true);
  useEffect(
    () => () => {
      mounted.current = false;
    },
    []
  );
  // Which scan an async answer belongs to: editing a parameter or cancelling moves it on.
  const runToken = useRef(0);

  const resetScan = () => {
    runToken.current += 1;
    setBusy(false);
    setScan(null);
    setMisses([]);
    setResults(null);
    setSelected({});
    setFailure(null);
  };

  /**
   * Editing a scan parameter invalidates everything the previous parameters produced.
   *
   * The results go, because `handleCreate` persists the suffix and quality as they are *now*:
   * keeping the tiles would let an operator approve a thumbnail probed with one suffix and create
   * cameras pointing somewhere else, in one pass and with no re-scan. A scan in flight is
   * abandoned too — it was triggered with the old parameters, so its images would not match the
   * form the operator is now looking at.
   *
   * @param {function} setter - The state setter for the edited field.
   * @returns {function} An input change handler.
   */
  const editScanParameter = (setter) => (event) => {
    const { value } = event.target;
    resetScan();
    setter(value);
  };

  /**
   * Change a connection parameter for this scan only.
   *
   * Recorded as an override; an untouched field dials the device's stored value. Nothing here is
   * written back to the device — these are scan parameters, not an edit form.
   *
   * @param {string} field - The scan-body field name.
   * @returns {function} An input change handler.
   */
  const editConnection = (field) => (event) => {
    const { value } = event.target;
    resetScan();
    setOverrides((prev) => ({ ...prev, [field]: value }));
  };

  /**
   * The value to display for a connection field: the operator's override, else the device's.
   *
   * @param {string} field - The scan-body field name.
   * @param {*} stored - The device's stored value.
   * @returns {string} A displayable value.
   */
  const connectionValue = (field, stored) => (overrides[field] !== undefined ? overrides[field] : String(stored ?? ""));

  /**
   * Scope a DOM id to this device.
   *
   * Nothing makes the panels mutually exclusive — `toggleDiscovery` keys them per device — so two
   * can be open at once. Hardcoded ids would duplicate, and every `Label for` would then point at
   * the first panel's input.
   *
   * @param {string} field - The field's local name.
   * @returns {string} An id unique to this panel.
   */
  const fieldId = (field) => `discovery-${field}-${device.public_id}`;

  // The device's brand kind unless the operator chose to scan as another type.
  const effectiveBrandKind = overrides.brand_kind !== undefined ? overrides.brand_kind : brandKind;
  const showSuffix = suffixApplies(effectiveBrandKind);
  // The template, unless the operator edited it for this scan.
  const suffixValue = overrides.rtsp_url_suffix !== undefined ? overrides.rtsp_url_suffix : derivedSuffix;
  const { serverName, source, reason } = resolveDeviceServerName(device, tree, machineCache);
  const { slots, error: rangesError } = parseSlotRanges(ranges);
  const suffixError = showSuffix ? validateSuffix(suffixValue) : null;
  const effectiveSuffix = showSuffix ? suffixValue : "";
  // The kinds offered by the "Device type" select, from the brands the host already fetched.
  // An override is only "set" once it actually differs from the device: typing a value back to
  // what was already there must not lock the create path.
  const identityOverridden = IDENTITY_OVERRIDES.filter((field) => {
    if (overrides[field] === undefined) return false;
    const stored = field === "brand_kind" ? brandKind : device[field];
    return String(overrides[field]) !== String(stored ?? "");
  });
  const credentialsOverridden = ["username", "password"].some(
    (field) => overrides[field] !== undefined && overrides[field] !== String(device[field] ?? "")
  );

  const availableKinds = useMemo(() => {
    const kinds = new Set(Object.values(brandKinds || {}).filter(Boolean));
    if (brandKind) kinds.add(brandKind);
    return [...kinds].sort();
  }, [brandKinds, brandKind]);

  /**
   * Slots already taken, as the *server* counts them.
   *
   * `CameraSerializer.validate` refuses a duplicate on store + host + rtsp_port + slot_number +
   * inference_machine — a store-scoped key, not a per-device one — and cameras created here carry
   * a null machine. Two capture devices can legitimately share a host and port, so a slot free on
   * this device but taken on its twin would look selectable and cost the whole atomic batch. Every
   * device at the same address therefore contributes. Slots are normalized to numbers: the scan
   * sends strings, the tree stores integers.
   */
  const existingSlots = useMemo(() => {
    const set = new Set();
    const take = (camera) => {
      const slot = Number(camera?.slot_number);
      if (Number.isFinite(slot)) set.add(slot);
    };

    // On this device, `UniqueConstraint(device, slot_number)` applies regardless of the machine:
    // an occupied slot is occupied.
    (device?.cameras || []).forEach(take);

    // On a device sharing the address, only a camera with **no** machine collides:
    // `CameraSerializer.validate` matches on `inference_machine` too, and states that a different
    // machine is allowed. Discovered cameras are created with a null machine, so locking a
    // sibling's slot that is bound to a machine would refuse a create the server would accept.
    (tree || [])
      .filter(
        (other) =>
          other?.public_id !== device?.public_id &&
          device?.host != null &&
          other?.host === device.host &&
          other?.rtsp_port === device?.rtsp_port
      )
      .forEach((sibling) => {
        (sibling?.cameras || []).filter((camera) => camera?.inference_machine == null).forEach(take);
      });

    return set;
  }, [device, tree]);

  const rows = useMemo(
    () => (results || []).map((hit) => ({ ...hit, existing: existingSlots.has(hit.slot) })),
    [results, existingSlots]
  );

  const selectedSlots = rows.filter((row) => !row.existing && selected[row.slot]).map((row) => row.slot);
  // `!creating`: a scan landing mid-create replaces the results and clears the selection under the
  // request in flight. `!scan`: one scan at a time, each capture costs the recorder a stream.
  // An IP-kind device with no suffix is refused: dashcam would find a path for the frame, but the
  // camera created from it would be saved without one and could not stream.
  const suffixMissing = showSuffix && !suffixValue;
  const canDiscover =
    Boolean(serverName) && !rangesError && !suffixError && !suffixMissing && !busy && !creating && !scan;

  const scanConnection = () => {
    const connection = { host: device.host, rtspPort: device.rtsp_port, apiPort: device.api_port };
    NUMERIC_CONNECTION_FIELDS.forEach((field) => {
      if (overrides[field] !== undefined && overrides[field] !== "") connection.rtspPort = Number(overrides[field]);
    });
    TEXT_CONNECTION_FIELDS.filter((field) => field !== "brand_kind").forEach((field) => {
      connection[field] = overrides[field] !== undefined ? overrides[field] : device[field];
    });
    return connection;
  };

  /**
   * An IP-kind device has no channel list to ask for: its suffix template is the path. A DVR is asked
   * once for its channels (`async/cameras`, restricted to the range); slots it does not have are not
   * probed, and the path of the stream matching the quality select is preferred.
   *
   * @returns {Promise<Map<number, string>|null>} slot → path (`""` lets dashcam discover it), or null
   *   when discovery failed or the scan went stale.
   */
  const pathsToProbe = async (target, isStale, streamType) => {
    if (showSuffix) return new Map(slots.map((slot) => [slot, expandSuffix(suffixValue, slot)]));
    const listed = await discoverChannels(
      { ...target, store_id: String(storeId), channels: slots.map((slot) => ({ slot_number: slot })) },
      // One key per click: an identical re-scan after fixing a link must not get the old answer.
      { idempotencyKey: `${device.public_id}-${Date.now()}`, isStale }
    );
    if (listed.status === "stale") return null;
    if (listed.status === "failed") {
      setFailure(`Channel discovery failed: ${listed.message}`);
      return null;
    }
    const paths = new Map();
    listed.cameras.forEach((row) => {
      const slot = Number(row?.channel);
      if (row?.not_found || !slots.includes(slot)) return;
      // Another stream's path would show a frame the created camera does not stream: dashcam
      // discovers the selected one instead.
      if (!paths.has(slot)) paths.set(slot, "");
      if (row.stream_type === streamType) paths.set(slot, row.rtsp_path || "");
    });
    return paths;
  };

  /**
   * Frames show up as they land. A slot dashcam reports as frameless is simply absent; a slot whose
   * capture failed is listed, since it says nothing about whether a camera is there.
   */
  const startScan = async () => {
    if (!canDiscover) return;
    resetScan();
    const token = runToken.current;
    const isStale = () => !mounted.current || runToken.current !== token;
    const streamType = streamQuality.toLowerCase();
    setBusy(true);
    setResults([]);
    try {
      const target = deviceTarget(scanConnection(), await resolveMachineRouting(serverName));
      if (isStale()) return;
      const paths = await pathsToProbe(target, isStale, streamType);
      if (!paths || isStale()) return;
      // Scan first: React 17 renders each update after an await, and `busy` off with no scan yet
      // would flash "No camera found" and re-enable Discover.
      setScan({ done: 0, total: paths.size });
      setBusy(false);
      await runPool(
        [...paths.keys()],
        MAX_IN_FLIGHT,
        async (slot) => {
          const rtspPath = paths.get(slot);
          const outcome = await captureFrame(
            {
              ...target,
              channel: { slot_number: slot, stream_type: streamType },
              ...(rtspPath ? { rtsp_path: rtspPath } : {}),
            },
            { isStale }
          );
          if (isStale()) return;
          if (outcome.status === "hit") {
            const hit = { slot, url: outcome.url, operationId: outcome.operationId };
            setResults((prev) => [...(prev || []), hit].sort((a, b) => a.slot - b.slot));
          } else if (outcome.status === "failed") {
            setMisses((prev) => [...prev, `Slot ${slot}: ${outcome.message}`]);
          }
          setScan((prev) => prev && { ...prev, done: prev.done + 1 });
        },
        isStale
      );
    } catch (error) {
      if (!isStale()) setFailure(errorText(error?.message, "The scan failed."));
    } finally {
      if (!isStale()) {
        setBusy(false);
        setScan(null);
      }
    }
  };

  const stopScan = () => {
    runToken.current += 1;
    setBusy(false);
    setScan(null);
    // Nothing found before cancelling is not "no camera found".
    setResults((prev) => (prev && prev.length > 0 ? prev : null));
  };

  /**
   * Turn every selected slot into a camera plus its single view, in one changeset.
   *
   * The ops are produced by `buildChangeset` from a working copy of the device rather than
   * hand-written, so create ordering, `client_ref` / `parent_ref` wiring and field filtering
   * stay in one place. The device itself is passed as its own server snapshot, so it diffs to
   * nothing and only the new children become ops.
   */
  const handleCreate = async () => {
    if (selectedSlots.length === 0) return;
    setCreating(true);
    setFailure(null);
    const newCameras = selectedSlots.map((slot) =>
      buildDiscoveredCamera({
        slot,
        suffix: effectiveSuffix,
        streamQuality,
        // The operator's pick, not a constant — the scan does not test it.
        protocol,
        // `effectiveBrandKind`, the same value `showSuffix` is computed from: deriving the mode from
        // the prop while the suffix field was decided by the override yields EXPLICIT with an empty
        // suffix, which nothing server-side refuses and which cannot stream. A brand-kind override
        // blocks creation anyway, so in practice the two agree.
        brandKind: effectiveBrandKind,
        publicId: tempId("cam"),
        viewPublicId: tempId("view"),
      })
    );

    const workingDevice = { ...device, cameras: [...(device.cameras || []), ...newCameras] };
    const payload = buildChangeset([device], [workingDevice]);
    // client_ref → slot, to name the offending slot when the server refuses one. Same trick as
    // `useStoreCameraTree.saveAll`, which indexes ops by op_id to resolve a failure back to the
    // entity the user acted on.
    const slotByRef = new Map(newCameras.map((camera) => [camera.public_id, camera.slot_number]));
    try {
      await postChangeset(payload);
      // The cameras were created either way, so the host is told to reload even if this panel is
      // already gone — only the local state updates are skipped.
      if (mounted.current) {
        setSelected({});
        setResults(null);
      }
      if (onApplied) onApplied();
    } catch (error) {
      if (!mounted.current) return;
      // Keep the selection: the operator retries rather than re-scanning.
      setFailure(changesetErrorMessage(error, payload.ops, slotByRef));
    } finally {
      if (mounted.current) setCreating(false);
    }
  };

  const toggle = (slot) => setSelected((prev) => ({ ...prev, [slot]: !prev[slot] }));

  return (
    <Card>
      <CardHeader>
        <CardTitle tag="h5" className="mb-0">
          Discover cameras on {device.name || device.host || device.public_id}
        </CardTitle>
      </CardHeader>
      <CardBody>
        {!serverName && <Alert color="warning">unable to scan, no server assigned — {reason}</Alert>}
        {serverName && source === "store" && (
          <Alert color="info">
            No camera on this device carries an inference machine yet — scanning via {serverName}, borrowed from another
            device of this store.
          </Alert>
        )}

        {/* Connection parameters, pre-filled from the device. Editable for the scan only — an
            operator probing a device whose stored credentials are wrong needs exactly this, and it
            is what the legacy card offered. */}
        <Row className="align-items-end">
          <Col md={3}>
            <Label for={fieldId("brand-kind")}>Device type</Label>
            <Input
              id={fieldId("brand-kind")}
              type="select"
              bsSize="sm"
              className="custom-select"
              value={effectiveBrandKind || ""}
              onChange={editConnection("brand_kind")}
            >
              {!effectiveBrandKind && <option value="">—</option>}
              {availableKinds.map((kind) => (
                <option key={kind}>{kind}</option>
              ))}
            </Input>
          </Col>
          <Col md={3}>
            <Label for={fieldId("host")}>Device IP</Label>
            <Input
              id={fieldId("host")}
              bsSize="sm"
              value={connectionValue("host", device.host)}
              onChange={editConnection("host")}
            />
          </Col>
          <Col md={2}>
            <Label for={fieldId("rtsp-port")}>RTSP port</Label>
            <Input
              id={fieldId("rtsp-port")}
              type="number"
              bsSize="sm"
              value={connectionValue("rtsp_port", device.rtsp_port)}
              onChange={editConnection("rtsp_port")}
            />
          </Col>
          <Col md={2}>
            <Label for={fieldId("username")}>Username</Label>
            <Input
              id={fieldId("username")}
              bsSize="sm"
              value={connectionValue("username", device.username)}
              onChange={editConnection("username")}
            />
          </Col>
          <Col md={2}>
            <Label for={fieldId("password")}>Password</Label>
            {/* The API returns it in clear for exactly this pre-fill; masking it costs nothing and
                keeps a DVR credential off a shared screen. */}
            <Input
              id={fieldId("password")}
              type="password"
              bsSize="sm"
              value={connectionValue("password", device.password)}
              onChange={editConnection("password")}
            />
          </Col>
        </Row>

        <Row className="align-items-end mt-2">
          <Col md={showSuffix ? 3 : 5}>
            <Label for={fieldId("ranges")}>Slot range</Label>
            <Input
              id={fieldId("ranges")}
              bsSize="sm"
              value={ranges}
              invalid={Boolean(rangesError)}
              onChange={editScanParameter(setRanges)}
              placeholder={DEFAULT_RANGES}
            />
          </Col>
          {showSuffix && (
            <Col md={3}>
              <Label for={fieldId("suffix")}>RTSP URL suffix</Label>
              <Input
                id={fieldId("suffix")}
                bsSize="sm"
                value={suffixValue}
                invalid={Boolean(suffixError)}
                onChange={editConnection("rtsp_url_suffix")}
                placeholder="/cam/{}/sub — {} is the slot"
              />
            </Col>
          )}
          <Col md={2}>
            <Label for={fieldId("protocol")}>Protocol</Label>
            <Input
              id={fieldId("protocol")}
              type="select"
              bsSize="sm"
              className="custom-select"
              value={protocol}
              onChange={editScanParameter(setProtocol)}
            >
              <option>TCP</option>
              <option>UDP</option>
            </Input>
          </Col>
          <Col md={2}>
            <Label for={fieldId("quality")}>Stream quality</Label>
            <Input
              id={fieldId("quality")}
              type="select"
              bsSize="sm"
              className="custom-select"
              value={streamQuality}
              onChange={editScanParameter(setStreamQuality)}
            >
              <option>SUB</option>
              <option>MAIN</option>
            </Input>
          </Col>
          <Col md={showSuffix ? 2 : 3}>
            <Button className="btn-info btn-block" disabled={!canDiscover} onClick={startScan}>
              {busy ? (
                <ThreeDots height="15" width="20" radius="5" color="#ffffff" ariaLabel="loading" visible />
              ) : (
                <i className="fa fa-binoculars mr-2" />
              )}
              Discover slots
            </Button>
          </Col>
        </Row>

        {rangesError && <p className="text-danger mt-2 mb-0">{rangesError}</p>}
        {suffixError && <p className="text-danger mt-2 mb-0">{suffixError}</p>}
        {slots.length > 0 && !rangesError && (
          <p className="text-muted mt-2 mb-0">
            {slots.length} slot{slots.length > 1 ? "s" : ""} will be probed.
          </p>
        )}
        {/* Legal on both sides — a one-slot explicit suffix is a real case — but without `{}`
            nothing varies per slot, so every probe hits the same URL and would create N cameras
            on one stream. Warn, don't block. */}
        {slots.length > 1 && showSuffix && suffixValue && !suffixError && !suffixValue.includes(SLOT_PLACEHOLDER) && (
          <p className="text-warning mt-2 mb-0">
            This suffix has no {SLOT_PLACEHOLDER} placeholder — every slot will probe the same URL.
          </p>
        )}
        {suffixMissing && (
          <p className="text-danger mt-2 mb-0">
            An RTSP suffix is required — the cameras created from this scan would otherwise have no stream path.
          </p>
        )}
        <p className="text-muted mt-2 mb-0" style={{ fontSize: "0.75rem" }}>
          The protocol is saved on the created cameras but not tested by the scan.
        </p>

        {failure && (
          <Alert color="danger" className="mt-3">
            {failure}
          </Alert>
        )}

        {(scan || busy) && (
          <Alert color="info" className="mt-3 d-flex justify-content-between align-items-center">
            <span>
              {scan
                ? `Scanning — ${scan.done}/${scan.total} slot${scan.total === 1 ? "" : "s"} checked.`
                : "Listing the device's channels…"}
            </span>
            <Button size="sm" outline color="secondary" onClick={stopScan}>
              Cancel
            </Button>
          </Alert>
        )}

        {misses.length > 0 && (
          <Alert color="warning" className="mt-3">
            {misses.length} slot{misses.length === 1 ? "" : "s"} could not be captured, so whether a camera is there is
            unknown:
            <ul className="mb-0">
              {misses.map((miss) => (
                // The dark theme colours list items white, unreadable on the warning background.
                <li key={miss} className="text-reset">
                  {miss}
                </li>
              ))}
            </ul>
          </Alert>
        )}

        {results !== null && rows.length === 0 && !scan && !busy && !failure && (
          <p className="text-muted mt-3 mb-0">No camera found on the probed slots.</p>
        )}

        {rows.length > 0 && (
          <>
            <div className="d-flex flex-wrap mt-3">
              {rows.map((row) => (
                <div key={row.slot} className="mr-3 mb-3 text-center" style={{ width: 160 }}>
                  <ProbeFrame url={row.url} operationId={row.operationId} alt={`Slot ${row.slot}`} />
                  <div className="mt-1">
                    <Input
                      type="checkbox"
                      id={fieldId(`slot-${row.slot}`)}
                      className="position-static m-0 mr-2 d-inline-block"
                      aria-label={`Slot ${row.slot}`}
                      disabled={row.existing}
                      checked={Boolean(selected[row.slot]) && !row.existing}
                      onChange={() => toggle(row.slot)}
                    />
                    <span>Slot {row.slot}</span>
                    {row.existing && (
                      <Badge color="secondary" className="ml-2">
                        existing
                      </Badge>
                    )}
                  </div>
                </div>
              ))}
            </div>
            {identityOverridden.length > 0 ? (
              <Alert color="warning" className="mb-0">
                These slots were probed with an overridden {identityOverridden.join(" and ")}, so they describe a box
                other than this capture device — there is nothing correct to create from them. Clear the override and
                scan again, or edit the device itself first.
              </Alert>
            ) : (
              <>
                {credentialsOverridden && (
                  <Alert color="info">
                    Probed with overridden credentials — the cameras created here will stream with the device&apos;s
                    stored credentials, so fix those too if the probe only worked with yours.
                  </Alert>
                )}
                <Button className="btn-info" disabled={creating || selectedSlots.length === 0} onClick={handleCreate}>
                  {creating ? (
                    <ThreeDots height="15" width="20" radius="5" color="#ffffff" ariaLabel="loading" visible />
                  ) : (
                    <i className="fa fa-plus mr-2" />
                  )}
                  Create {selectedSlots.length} camera{selectedSlots.length === 1 ? "" : "s"}
                </Button>
              </>
            )}
          </>
        )}
      </CardBody>
    </Card>
  );
}

DeviceDiscoveryPanel.propTypes = {
  storeId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
  device: PropTypes.shape({
    public_id: PropTypes.string.isRequired,
    name: PropTypes.string,
    host: PropTypes.string,
    rtsp_port: PropTypes.number,
    api_port: PropTypes.number,
    username: PropTypes.string,
    password: PropTypes.string,
    cameras: PropTypes.arrayOf(
      PropTypes.shape({
        slot_number: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
        rtsp_url_suffix: PropTypes.string,
      })
    ),
  }).isRequired,
  tree: PropTypes.arrayOf(PropTypes.shape({ public_id: PropTypes.string })),
  machineCache: PropTypes.objectOf(PropTypes.string),
  /** `RecorderBrand.kind` of the device's brand — decides whether a suffix is offered. */
  brandKind: PropTypes.string,
  /** Brand id → kind, used to populate the "Device type" select. */
  brandKinds: PropTypes.objectOf(PropTypes.string),
  /** Called once a changeset landed, so the host can refetch the tree. */
  onApplied: PropTypes.func,
};
